import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '@/services/api';

export interface GeneralSettings {
  systemName: string;
  logoUrl: string;
  dateFormat: string;
  timeFormat: string;
  timezone: string;
  currency: string;
}

export interface AssetSettings {
  autoGenerateAssetTags: boolean;
  assetTagPrefix: string;
  assetTagLength: number;
  requireSerialNumber: boolean;
  enableDepreciationTracking: boolean;
  defaultDepreciationMethod: string;
}

export interface NotificationSettings {
  enableEmailNotifications: boolean;
  warrantyExpiryAlertDays: number;
  maintenanceReminders: boolean;
  ticketUpdateNotifications: boolean;
  lowStockAlerts: boolean;
  documentExpiryAlerts: boolean;
}

export interface TicketSettings {
  autoAssignTickets: boolean;
  defaultPriority: string;
  requireCategory: boolean;
  allowPublicTicketSubmission: boolean;
  slaBreachNotifications: boolean;
}

export interface AllSettings {
  general: GeneralSettings;
  assets: AssetSettings;
  notifications: NotificationSettings;
  tickets: TicketSettings;
}

const defaultSettings: AllSettings = {
  general: {
    systemName: 'Glide',
    logoUrl: '',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24h',
    timezone: 'Africa/Nairobi',
    currency: 'UGX',
  },
  assets: {
    autoGenerateAssetTags: true,
    assetTagPrefix: 'AST',
    assetTagLength: 6,
    requireSerialNumber: false,
    enableDepreciationTracking: false,
    defaultDepreciationMethod: 'straight-line',
  },
  notifications: {
    enableEmailNotifications: true,
    warrantyExpiryAlertDays: 30,
    maintenanceReminders: true,
    ticketUpdateNotifications: true,
    lowStockAlerts: true,
    documentExpiryAlerts: true,
  },
  tickets: {
    autoAssignTickets: false,
    defaultPriority: 'MEDIUM',
    requireCategory: true,
    allowPublicTicketSubmission: true,
    slaBreachNotifications: true,
  },
};

interface SettingsState {
  settings: AllSettings;
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
  
  // Actions
  loadSettings: () => Promise<void>;
  updateSettings: (settings: AllSettings) => void;
  getCurrency: () => string;
  getDateFormat: () => string;
  getTimeFormat: () => string;
  getTimezone: () => string;
}

// Currency code to locale mapping
const currencyLocaleMap: Record<string, string> = {
  UGX: 'en-UG',
  KES: 'en-KE',
  USD: 'en-US',
  EUR: 'de-DE',
  GBP: 'en-GB',
  TZS: 'en-TZ',
  RWF: 'en-RW',
  NGN: 'en-NG',
  ZAR: 'en-ZA',
  EGP: 'en-EG',
};

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set, get) => ({
      settings: defaultSettings,
      isLoading: false,
      isLoaded: false,
      error: null,

      loadSettings: async () => {
        // Don't reload if already loaded
        if (get().isLoaded) return;
        
        set({ isLoading: true, error: null });
        try {
          const response = await api.get('/settings');
          const data = response.data?.data || response.data;
          if (data) {
            set({
              settings: {
                general: { ...defaultSettings.general, ...data.general },
                assets: { ...defaultSettings.assets, ...data.assets },
                notifications: { ...defaultSettings.notifications, ...data.notifications },
                tickets: { ...defaultSettings.tickets, ...data.tickets },
              },
              isLoading: false,
              isLoaded: true,
            });
          } else {
            set({ isLoading: false, isLoaded: true });
          }
        } catch (error) {
          console.error('Failed to load settings:', error);
          set({ isLoading: false, isLoaded: true, error: 'Failed to load settings' });
        }
      },

      updateSettings: (settings: AllSettings) => {
        set({ settings });
      },

      getCurrency: () => get().settings.general.currency,
      getDateFormat: () => get().settings.general.dateFormat,
      getTimeFormat: () => get().settings.general.timeFormat,
      getTimezone: () => get().settings.general.timezone,
    }),
    {
      name: 'settings-storage',
      partialize: (state) => ({
        settings: state.settings,
        isLoaded: state.isLoaded,
      }),
    }
  )
);

// Helper function to format currency using settings
export function formatCurrencyWithSettings(amount: number, currencyCode?: string): string {
  const currency = currencyCode || useSettingsStore.getState().settings.general.currency;
  const locale = currencyLocaleMap[currency] || 'en-US';
  
  // Determine decimal places based on currency
  const noDecimalCurrencies = ['UGX', 'KES', 'TZS', 'RWF', 'JPY'];
  const minimumFractionDigits = noDecimalCurrencies.includes(currency) ? 0 : 2;
  const maximumFractionDigits = noDecimalCurrencies.includes(currency) ? 0 : 2;
  
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(amount);
}
